// SuperDashboardPage — /super.
//
// Cross-tenant overview for platform super-admins (realm='super', logged
// in via /super/login). Six KPI tiles drawn from the system-health
// endpoint (companies counts + message-queue snapshot) plus links into
// the deeper /super/companies and /super/system pages.

import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ListOrdered,
  Mail,
  ShieldX,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { Button } from '../../components/ui/Button/Button';
import { useSystemHealth } from './hooks/useSuper';
import styles from './SuperDashboardPage.module.css';

interface KpiTile {
  label: string;
  value: string;
  tone: 'blue' | 'green' | 'amber' | 'red' | 'teal' | 'purple';
  icon: ComponentType<LucideProps>;
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-GB').format(n);
}

export function SuperDashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const health = useSystemHealth();

  const tiles: KpiTile[] = health.data
    ? [
        {
          label: 'Total Companies',
          value: formatNumber(health.data.companies.total),
          tone: 'blue',
          icon: Building2,
        },
        {
          label: 'Active',
          value: formatNumber(health.data.companies.active),
          tone: 'green',
          icon: CheckCircle2,
        },
        {
          label: 'Suspended',
          value: formatNumber(health.data.companies.suspended),
          tone: 'red',
          icon: ShieldX,
        },
        {
          label: 'Trial',
          value: formatNumber(health.data.companies.trial),
          tone: 'amber',
          icon: AlertTriangle,
        },
        {
          label: 'Queue Pending',
          value: formatNumber(health.data.message_queue.pending),
          tone: 'teal',
          icon: ListOrdered,
        },
        {
          label: 'Failed (last 24h)',
          value: formatNumber(health.data.message_queue.failed),
          tone: 'purple',
          icon: Mail,
        },
      ]
    : [];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Superadmin"
        subtitle="Platform management — every tenant, every metric"
      />

      {health.isLoading ? (
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      ) : null}

      {tiles.length > 0 ? (
        <ul className={styles.kpiGrid}>
          {tiles.map((t) => (
            <li
              key={t.label}
              className={`${styles.kpi} ${styles[`tone_${t.tone}`]}`}
            >
              <span className={styles.kpiIcon} aria-hidden>
                <t.icon size={20} aria-hidden />
              </span>
              <span className={styles.kpiBody}>
                <span className={styles.kpiLabel}>{t.label}</span>
                <span className={styles.kpiValue}>{t.value}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className={styles.linkGrid}>
        <Card title="Companies">
          <p className={styles.linkDesc}>
            List, search and manage every tenant. Change tier or status, or
            impersonate as an admin to debug live customer issues.
          </p>
          <Link to="/super/companies" className={styles.linkRow}>
            <Button variant="primary">Open companies →</Button>
          </Link>
        </Card>

        <Card title="Admins">
          <p className={styles.linkDesc}>
            Manage other platform admins. Suspend, reset passwords, or create
            new admin accounts.
          </p>
          <Button variant="primary" onClick={() => navigate('/super/admins')}>
            Open admins →
          </Button>
        </Card>

        <Card title="System health">
          <p className={styles.linkDesc}>
            Message-queue snapshot, cron status, server uptime. Auto-refreshes
            every 30 s.
          </p>
          <Button variant="primary" onClick={() => navigate('/super/system')}>
            Open system →
          </Button>
        </Card>
      </div>
    </div>
  );
}
